<template>
  <div
    v-click-outside="close"
    class="bg-picker"
  >
    <button
      v-if="!isEditMode"
      class="bg-picker-options__trigger"
      :class="{ 'is-open': isOpen }"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="true"
      @click="toggle"
    >
      <i class="bi bi-three-dots-vertical" />
    </button>
    <div
      v-if="isEditMode"
      class="display-builder-edit-actions"
    >
      <button
        class="dbh-btn dbh-btn--outline display-builder-edit-actions__cancel text-sm"
        type="button"
        @click="cancelEditLayout"
      >
        <i class="bi bi-x-lg" /> Cancel
      </button>

      <div class="display-builder-layout-save-menu">
        <div class="dbh-split-btn">
          <button
            class="dbh-btn dbh-btn--primary dbh-split-btn__main display-builder-edit-actions__done text-sm"
            type="button"
            @click="finishEditLayout"
          >
            <i class="bi bi-floppy" /> Save Layout
          </button>
          <button
            class="dbh-btn dbh-btn--primary dbh-split-btn__caret display-builder-edit-actions__done text-sm"
            type="button"
            aria-label="More layout save options"
            :aria-expanded="isLayoutSaveMenuOpen.toString()"
            @click.stop="toggleLayoutSaveMenu"
          >
            <i class="bi bi-chevron-down" />
          </button>
        </div>
        <div
          v-if="isLayoutSaveMenuOpen"
          class="display-builder-layout-save-menu__panel"
        >
          <button
            type="button"
            class="display-builder-layout-save-menu__item text-sm"
            @click="saveAsNewLayout"
          >
            <i class="bi bi-plus-lg" />
            Save as new Layout...
          </button>
        </div>
      </div>
    </div>
    <transition name="bg-picker__panel-options">
      <div
        v-if="isOpen"
        class="bg-picker__panel-options"
        role="menu"
        :style="panelPositionStyle"
      >
        <div class="section-header">
          Layout
        </div>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="newLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-plus h-4 w-4 mr-2"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          New Layout
        </button>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="editLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-pencil h-4 w-4 mr-2"
          >
            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
            <path d="m15 5 4 4" />
          </svg>
          Edit Layout
        </button>
        <div class="separator w-100" />
        <div class="section-header">
          Save
        </div>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="saveAsNewLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-plus h-4 w-4 mr-2"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          Save as new layout...
        </button>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="updateBaseLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-save h-4 w-4 mr-2"
          >
            <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
            <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
            <path d="M7 3v4a1 1 0 0 0 1 1h7" />
          </svg>
          Update base layout
        </button>
        <button
          :disabled="!canManageDefaults"
          class="bg-picker__option"
          role="menuitem"
          @click="saveAsDefault"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-save h-4 w-4 mr-2"
          >
            <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
            <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
            <path d="M7 3v4a1 1 0 0 0 1 1h7" />
          </svg>
          Set as startup layout
        </button>
        <div class="separator w-100" />
        <div class="section-header">
          File
        </div>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="exportLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-download h-4 w-4 mr-2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line
              x1="12"
              x2="12"
              y1="15"
              y2="3"
            />
          </svg>
          Export layout...
        </button>
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="$refs.displayBuilderImportInput.click()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-upload h-4 w-4 mr-2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line
              x1="12"
              x2="12"
              y1="3"
              y2="15"
            />
          </svg>
          Import layout...
        </button>
        <input
          ref="displayBuilderImportInput"
          type="file"
          accept="application/json,.json"
          class="d-none"
          @change="importDisplayBuilderLayout"
        >
        <div class="separator w-100" />
        <div class="section-header">
          Share
        </div>
        <button
          class="bg-picker__option"
          role="menuitem"
          :disabled="isCopyingLink"
          @click="copyProofLink"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-link2 h-4 w-4 mr-2"
          >
            <path d="M9 17H7A5 5 0 0 1 7 7h2" />
            <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
            <line
              x1="8"
              x2="16"
              y1="12"
              y2="12"
            />
          </svg>
          {{ isCopyingLink ? 'Creating link...' : 'Copy link' }}
        </button>
        <button
          :disabled="true"
          class="bg-picker__option"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-mail h-4 w-4 mr-2"
          >
            <rect
              width="20"
              height="16"
              x="2"
              y="4"
              rx="2"
            />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          Email...
        </button>
        <div class="separator w-100" />
        <button
          class="bg-picker__option"
          role="menuitem"
          @click="resetLayout"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-rotate-ccw h-4 w-4 mr-2"
          >
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Reset canvas
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';

export default {
  name: 'DisplayBuilderOptions',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) binding.value(event);
        };
        document.addEventListener('mousedown', el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._clickOutsideHandler);
      },
    },
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    placement: {
      type: String,
      default: 'bottom-right',
      validator: (v) => ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(v),
    },
  },
  data() {
    return {
      isOpen: false,
      isLayoutSaveMenuOpen: false,
      currentBackground: this.value || null,
      isCopyingLink: false,
    };
  },

  computed: {
    ...mapGetters('displayBuilder', ['isEditMode']),
    swatchStyle() {
      if (!this.currentBackground) return {};
      return {
        backgroundImage: `url(${this.currentBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },

    panelPositionStyle() {
      const map = {
        'bottom-right': { top: 'calc(100% + 6px)', right: '0' },
        'bottom-left':  { top: 'calc(100% + 6px)', left: '0' },
        'top-right':    { bottom: 'calc(100% + 6px)', right: '0' },
        'top-left':     { bottom: 'calc(100% + 6px)', left: '0' },
      };
      return map[this.placement] || map['bottom-right'];
    },
    canManageDefaults() {
      return !!window.can_save;
    },
  },

  watch: {
    value(v) {
      this.currentBackground = v;
    },
    isEditMode(value) {
      document.body.classList.toggle('display-builder-editing-layout', !!value);
    },
  },
  mounted() {
    document.body.classList.toggle('display-builder-editing-layout', !!this.isEditMode);
  },

  beforeDestroy() {
    document.body.classList.remove('display-builder-editing-layout');
  },

  methods: {
    ...mapActions('displayBuilder', [
      'enableEditMode',
      'disableEditMode',
    ]),
    toggle() {
      this.isOpen = !this.isOpen;
    },

    close() {
      this.isOpen = false;
      this.isLayoutSaveMenuOpen = false;
    },
    toggleLayoutSaveMenu() {
      this.isLayoutSaveMenuOpen = !this.isLayoutSaveMenuOpen;
    },
    editLayout() {
      this.enableEditMode();
      this.$emit('edit-layout');
      this.close();
    },
    async newLayout() {
      this.close();

      const started = await window.displayBuilderStartNewLayout?.();

      if (!started) {
        return;
      }

      this.enableEditMode();
      this.$emit('edit-layout');
    },
    async cancelEditLayout() {
      window.displayBuilderPanel?.cancelLayoutEdit?.();
      this.disableEditMode();
      this.close();
    },
    async finishEditLayout() {
      this.isLayoutSaveMenuOpen = false;

      if (window.displayBuilderCurrentLayoutId) {
        try {
          await window.displayBuilderSaveLayoutPreset?.();
          window.displayBuilderPanel?.finishLayoutEdit?.();
          this.disableEditMode();
          this.close();
        } catch (error) {
          console.error('[DisplayBuilder] Layout update failed', error);
          toastr.error('Could not save layout');
        }

        return;
      }

      this.saveAsNewLayout();
    },
    saveAsNewLayout() {
      const shouldFinishEditing = this.isEditMode;
      const activeVersionId = Number(window.displayBuilderCurrentVersionId);
      const shouldPreserveVersion = !shouldFinishEditing
        && Number.isFinite(activeVersionId)
        && activeVersionId > 0;

      this.close();
      window.dispatchEvent(new CustomEvent('display-builder:new-version-modal-open', {
        detail: {
          type: 'layout',
          saveOptions: shouldPreserveVersion
            ? {
              preserveActiveVersion: true,
              activeVersionId,
            }
            : {},
          onSaved: async () => {
            if (!shouldFinishEditing) {
              return;
            }

            window.displayBuilderPanel?.finishLayoutEdit?.();
            this.disableEditMode();
            this.close();
          },
        },
      }));
    },
    async updateBaseLayout() {
      let baseLayoutId = Number(
        window.displayBuilderBaseLayoutId || window.displayBuilderCurrentLayoutId
      );
      const activeVersionId = Number(window.displayBuilderCurrentVersionId);

      this.close();

      if (!Number.isFinite(baseLayoutId) || baseLayoutId <= 0) {
        baseLayoutId = await this.selectBaseLayoutForVersion();

        if (!baseLayoutId) {
          return;
        }
      }

      if (typeof window.displayBuilderSaveLayoutPreset !== 'function') {
        toastr.error('Display Builder layout saving is not ready yet.');
        return;
      }

      try {
        await window.displayBuilderSaveLayoutPreset({
          layoutItemId: baseLayoutId,
          requireExisting: true,
          preserveActiveVersion: Number.isFinite(activeVersionId) && activeVersionId > 0,
          activeVersionId: Number.isFinite(activeVersionId) && activeVersionId > 0
            ? activeVersionId
            : null,
        });
      } catch (error) {
        console.error('[DisplayBuilder] Base layout update failed', error);
        toastr.error('Could not update base layout');
      }
    },
    async selectBaseLayoutForVersion() {
      const layouts = Array.isArray(window.displayBuilderSavedItems?.layouts)
        ? window.displayBuilderSavedItems.layouts
        : [];

      if (!layouts.length) {
        toastr.warning('Save a layout before updating its base.');
        return null;
      }

      if (layouts.length === 1) {
        return Number(layouts[0].id);
      }

      const inputOptions = layouts.reduce((options, layout) => {
        options[layout.id] = layout.name;
        return options;
      }, {});
      const result = await Swal.fire({
        title: 'Choose base layout',
        text: 'This version was saved before base-layout tracking was available.',
        input: 'select',
        inputOptions,
        inputPlaceholder: 'Select a layout',
        showCancelButton: true,
        confirmButtonText: 'Update layout',
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-2',
        },
        inputValidator: (value) => value ? null : 'Select a layout to update.',
      });

      return result.isConfirmed ? Number(result.value) : null;
    },
    async resetLayout() {
      const isConfirmed = typeof window.displayBuilderConfirm === 'function'
        ? await window.displayBuilderConfirm({
          message: 'Reset the canvas? Unsaved changes on the current canvas will be lost.',
          okText: 'Reset',
        })
        : (await Swal.fire({
          text: 'Reset the canvas? Unsaved changes on the current canvas will be lost.',
          showCancelButton: true,
          confirmButtonText: 'Reset',
          cancelButtonText: 'Cancel',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary ml-2',
          },
        })).isConfirmed;

      if (!isConfirmed) {
        return;
      }

      this.close();

      try {
        $('.loadingOverlay').fadeIn();
        $('.loadingOverlay-text').text('Resetting Display Builder...');

        const { data } = await axios.post(`/display-builder/0/${window.layoutId}/reset`, {
          version_id: window.displayBuilderCurrentVersionId || null,
        });

        if (data.item?.id) {
          window.displayBuilderCurrentVersionId = Number(data.item.id);
          window.displayBuilderSetCurrentLayoutId?.(null);
          window.displayBuilderSetBaseLayoutId?.(null);
        }

        if (data.state && typeof window.displayBuilderApplyState === 'function') {
          await window.displayBuilderApplyState(data.state);
        }

        await window.displayBuilderRefreshSavedItems?.();
        toastr.success(data.message || 'Display Builder reset');
      } catch (error) {
        console.error('[DisplayBuilder] Reset failed', error);
        toastr.error('Failed to reset Display Builder');
      } finally {
        $('.loadingOverlay').fadeOut();
      }
    },
    async saveAsDefault() {
      this.close();
      try {
        const changes = this.getDisplayBuilderPendingChanges();

        await axios.post(`/display-builder/0/${window.layoutId}/save-default`, {
          changes,
        });

        toastr.success('Display Builder default saved for this company');
      } catch (error) {
        console.error('[DisplayBuilder] Save default failed', error);
        toastr.error('Could not save Display Builder default');
      }
    },
    getDisplayBuilderPendingChanges() {
      const { layoutChanges } = ShowPageScopeCollector.get(['layoutChanges']);
      const changes = Array.isArray(layoutChanges)
        ? layoutChanges.map((change) => ({
          instance_id: change.instance_id,
          key: change.key,
          value: change.value,
        }))
        : [];

      if (!window.displayBuilderPanel) {
        return changes;
      }

      window.displayBuilderPanel.markDisplayBuilderDirty?.flush?.();

      const instanceId = window.displayBuilderInstanceId || window.previews?.[0]?.id;

      if (!instanceId) {
        return changes;
      }

      const displayBuilderState = window.displayBuilderPanel.buildDisplayBuilderStateFromPanel();
      const existingIndex = changes.findIndex((change) => {
        return change.instance_id === instanceId && change.key === 'display_builder';
      });

      const change = {
        instance_id: instanceId,
        key: 'display_builder',
        value: displayBuilderState,
      };

      if (existingIndex !== -1) {
        changes.splice(existingIndex, 1, change);
      } else {
        changes.push(change);
      }

      return changes;
    },
    async importDisplayBuilderLayout(event) {
      const file = event.target.files?.[0];

      event.target.value = '';

      if (!file) {
        return;
      }

      const canContinue = await window.displayBuilderConfirmUnsavedAction?.({
        message: 'Import this layout? Unsaved changes on the current canvas will be lost.',
      });

      if (canContinue === false) {
        return;
      }

      try {
        $('.loadingOverlay').fadeIn();
        const formData = new FormData();
        formData.append('file', file);

        $('.loadingOverlay-text').text('Importing Display Builder layout...');

        const response = await axios.post(`/display-builder/0/${window.layoutId}/import-layout`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        toastr.success(response.data?.message || 'Layout imported successfully');

        window.displayBuilderAllowNextNavigation = true;
        window.location.reload();
      } catch (error) {
        console.error('[DisplayBuilder] Import failed', error);
        toastr.error(error.response?.data?.message || 'Could not import layout');
      } finally {
        $('.loadingOverlay').fadeOut();
      }
    },
    exportLayout() {
      this.close();
      window.location.href = `/display-builder/0/${window.layoutId}/export-layout`;
    },
    async copyProofLink() {
      if (this.isCopyingLink) {
        return;
      }

      this.isCopyingLink = true;
      this.close();

      try {
        const { saveShareProof } = ShowPageScopeCollector.get(['saveShareProof']);

        if (typeof saveShareProof !== 'function') {
          throw new Error('Share proof handler is not available');
        }

        $('.loadingOverlay').fadeIn();
        $('.loadingOverlay-text').text('Creating share proof link...');

        const proofData = await saveShareProof();

        const link = proofData?.url || $('#proof_copyTarget').val() || $('#proof_base_share_url').val();

        if (!link) {
          throw new Error('Share proof link was not created');
        }

        await this.copyTextToClipboard(link);
        toastr.success('Share proof link copied');
      } catch (error) {
        console.error('[DisplayBuilder] Copy proof link failed', error);
        toastr.error(error?.message || 'Could not copy share proof link');
      } finally {
        this.isCopyingLink = false;
        $('.loadingOverlay').fadeOut();
      }
    },
    async copyTextToClipboard(text) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        const copied = document.execCommand('copy');

        if (!copied) {
          throw new Error('Copy failed');
        }
      } finally {
        document.body.removeChild(textarea);
      }
    }
  },
};
</script>

<style scoped>
.bg-picker {
  --bg-picker-teal:      #2eb89a;
  --bg-picker-teal-dim:  rgba(46, 184, 154, 0.12);
  --bg-picker-border:    #e4e7eb;
  --bg-picker-bg:        #ffffff;
  --bg-picker-surface:   #f8f9fb;
  --bg-picker-hover:     #f2f4f7;
  --bg-picker-text:      #1a1d23;
  --bg-picker-muted:     #6b7280;
  --bg-picker-light:     #9ca3af;
  --bg-picker-danger:    #ef4444;
  --bg-picker-danger-bg: #fef2f2;
  --bg-picker-radius:    8px;
  --bg-picker-radius-sm: 5px;
  --bg-picker-shadow:    0 6px 20px rgba(0,0,0,.11), 0 1px 4px rgba(0,0,0,.07);
  --bg-picker-font:      'DM Sans', system-ui, sans-serif;
}

.bg-picker {
  position: relative;
  display: inline-flex;
  font-family: var(--bg-picker-font);
}

.bg-picker-options__trigger {
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-picker-bg);
  border: 1px solid #e4e7eb;
  border-radius: var(--bg-picker-radius-sm);
  color: var(--bg-picker-muted);
  cursor: pointer;
  padding: 0;
  transition: background 0.14s, border-color 0.14s, color 0.14s, box-shadow 0.14s;
  flex-shrink: 0;
  overflow: hidden;
  padding: 0.5rem 1rem;
  font-size: 13px;
  line-height: 1rem;
}

.bg-picker-options__trigger:hover {
  background: var(--bg-picker-surface);
  border-color: #c9cdd4;
  color: var(--bg-picker-text);
}

.bg-picker__swatch {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: calc(var(--bg-picker-radius-sm) - 1px);
}

.bg-picker__panel-options {
  position: absolute;
  z-index: 1050;
  width: 14rem;
  min-width: 8rem;
  background: var(--bg-picker-bg);
  border: 1px solid var(--bg-picker-border);
  border-radius: var(--bg-picker-radius);
  box-shadow: var(--bg-picker-shadow);
  padding: 10px 0px;
  outline: none;
}

.bg-picker__panel-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--bg-picker-light);
  margin: 0;
  padding: 4px 12px 8px;
}

.bg-picker__option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--bg-picker-text);
  background: transparent;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  line-height: 1.3;
}

.bg-picker__option:disabled {
  opacity: 0.5;
}

.bg-picker__option:hover {
  background: var(--bg-picker-hover);
}

.bg-picker__option--danger {
  color: var(--bg-picker-danger);
}

.bg-picker__option--danger:hover {
  background: var(--bg-picker-danger-bg);
}

.bg-picker__option-icon {
  display: flex;
  align-items: center;
  color: var(--bg-picker-muted);
  flex-shrink: 0;
}

.bg-picker__option--danger .bg-picker__option-icon {
  color: var(--bg-picker-danger);
}

.bg-picker__file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.bg-picker__panel-enter-active,
.bg-picker__panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.bg-picker__panel-enter,
.bg-picker__panel-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.97);
}
.dbh-btn--primary {
    background: var(--teal);
    border-color: var(--teal);
    color: #fff;
}
.dbh-btn--primary:hover {
    background: var(--teal-dark);
    border-color: var(--teal-dark);
    box-shadow: 0 2px 8px rgba(46,184,154,.3);
}
.dbh-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font);
    font-size: 13px;
    font-weight: 500;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    /* padding: 7px 13px; */
    padding: 0.5rem 1rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
    line-height: 1;
}
.display-builder-edit-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.display-builder-edit-actions .dbh-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: .875rem;
  line-height: 1.25rem;
  white-space: nowrap;
}

.display-builder-edit-actions__cancel {
  background: #ffffff;
  border-color: var(--bg-picker-border);
  color: var(--bg-picker-text);
}

.display-builder-edit-actions__cancel:hover {
  background: var(--bg-picker-surface);
  border-color: #c9cdd4;
}

.display-builder-edit-actions__done {
  background: var(--teal);
  border-color: var(--teal);
  color: #ffffff;
}

.display-builder-edit-actions__done:hover {
  background: var(--teal-dark);
  border-color: var(--teal-dark);
}

.display-builder-layout-save-menu {
  position: relative;
}

.dbh-split-btn {
  display: inline-flex;
  position: relative;
}

.dbh-split-btn__main {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.dbh-split-btn__caret {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
  border-left-color: rgba(255,255,255,.28) !important;
}

.display-builder-layout-save-menu__panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 224px;
  background: #ffffff;
  border: 1px solid var(--bg-picker-border);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(16, 24, 40, 0.16);
  padding: 6px;
  z-index: 1060;
}

.display-builder-layout-save-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  border-radius: 6px;
  background: #ffffff;
  color: var(--bg-picker-text);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.display-builder-layout-save-menu__item:hover {
  background: var(--bg-picker-surface);
}
.separator {
  height: 1px;
  background-color: #f1f5f9;
  margin: 0.25rem 0;
}
.bg-picker__panel-options button {
  font-size: .875rem !important;
  line-height: 1.25rem !important;
}
.bg-picker__panel-options button:last-child {
  color: #ef4444 !important;
}
.w-4 {
  width: 1rem !important;
}
.h-4 {
  height: 1rem !important;
}
.section-header {
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  font-weight: 500;
  font-size: 10px;
  padding: 0.25rem 12px !important;
}
</style>
